import React, { useEffect, useRef, useState } from 'react';
// react-select/async unresolves on v1.2.1, which is used on web-app in referrals form
// when web-app starts using patientForms, we will need to migrate from 1.2.1 to 3.0.4 on referrals form
import ReactSelect, { FocusEventHandler, Props as ReactSelectProps } from 'react-select';
import { useIntl } from 'react-intl';

import { withStyles } from '@material-ui/core';

import SelectControl from './SelectControl';
import SelectOption from './SelectOption';
import messages from './Select.messages';

const styles = () => ({
  input: {
    display: 'flex',

    '& input': {
      opacity: `1 !important`,
      width: '100% !important', // it will prevent a run time style from react-select to change input width
    },
  },
});

// react-select api for styles. Component is used on ItemDropdown, ItemPharmacySearch & ItemMedicationSearch
const customStyles = {
  valueContainer: (provided: any) => ({
    ...provided,
    alignItems: 'flex-start',
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    ' > div:last-child': {
      width: '100%',
    },
    ' > div:last-child > div': {
      width: '100%',
    },
  }),
  singleValue: (provided: any) => {
    const color = '#182538';
    const fontSize = 17;
    const fontWeight = 500;
    const paddingBottom = 4;
    const width = 'calc(100% - 32px)';

    return { ...provided, color, fontSize, fontWeight, paddingBottom, width };
  },
  multiValue: (provided: any) => {
    // The component api uses the fontSize * 0.85 on the multivalue. 16.5 * 0.85 = 14 px
    const fontSize = 16.5;
    const fontWeight = 500;

    return { ...provided, fontSize, fontWeight };
  },
  placeholder: (provided: any) => {
    const fontSize = 14;
    const fontWeight = 500;
    const paddingBottom = 4;
    const color = '#4e6180';

    return { ...provided, fontSize, fontWeight, paddingBottom, color };
  },
  input: (provided: any) => ({ ...provided, display: 'flex', flex: 1, paddingTop: 4 }),
};

export type TSelectOption = { label: string; value: string };

export interface SelectProps extends Omit<ReactSelectProps, 'defaultValue'> {
  defaultValue?: string;
  shouldAllowFreeText: boolean;
  freeText?: boolean;
}

function Select({
  components,
  loadOptions,
  filterOption,
  loadingMessage,
  noOptionsMessage,
  isClearable,
  isMulti,
  defaultValue,
  isNumberOnly,
  shouldAutoFocus,
  maxLength = Number.POSITIVE_INFINITY,
  onChange,
  breakWhiteSpace,
  shouldAllowFreeText = false,
  value,
  freeText = true,
  ...other
}: SelectProps) {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [options, setOptions] = useState<TSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const innerRef = useRef<any>();
  const currentOption = React.useRef(defaultValue);
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (innerRef && shouldAutoFocus) {
      innerRef.current.focus();
    }
  }, [innerRef, shouldAutoFocus]);

  // workaround to keep search value on input when on blur
  const onInputChange = (_value: string, action: { action: string }) => {
    let valueCopy = _value;

    if (isNumberOnly) {
      valueCopy = valueCopy.replace(/\D/, '');
    }

    if (valueCopy.length > maxLength) {
      return;
    }

    // action can be null on react-select v1.x.x used on web-app
    if (!['input-blur', 'menu-close'].includes((action || {}).action)) {
      setInputValue(valueCopy);
      if (shouldAllowFreeText && onChange) {
        onChange({
          label: valueCopy,
          value: '',
        });
      }
      if (loadOptions) {
        setLoading(true);
        loadOptions(valueCopy, (optionsValue: any) => {
          setLoading(false);
          setOptions(optionsValue);
        });
      }
    }
  };

  const handleOptionSelect = (_value: any) => {
    if (!_value) {
      setInputValue(undefined);
    }

    if (_value && 'label' in _value) {
      currentOption.current = _value.label;
      setInputValue(_value.label);
    }

    if (onChange) {
      if (!isMulti) {
        currentOption.current = undefined;
        setInputValue(undefined);
      }
      onChange(_value);
    }
  };

  const defaultLoadingMessage = () => formatMessage(messages.serach);
  const defaultNoOptionsMessage = () => formatMessage(messages.noOptions);
  const defaultPlaceholder = formatMessage(messages.placeholder);

  const onBlur: FocusEventHandler = (args) => {
    other.onBlur?.(args);
    // reset the inputValue to the current selectedValue
    if (!freeText && currentOption.current !== inputValue) {
      setInputValue(currentOption.current);
    }
  };

  const componentsExtended: any = {
    Control: SelectControl,
    Option: (optionProps: any) => <SelectOption breakWhiteSpace={breakWhiteSpace} {...optionProps} />,
    DropdownIndicator: () => <div />,
    IndicatorSeparator: () => <div />,
    ...components,
  };

  return (
    <ReactSelect
      ref={innerRef}
      styles={customStyles}
      options={options}
      components={componentsExtended}
      inputValue={inputValue}
      onInputChange={onInputChange}
      filterOption={loadOptions ? () => true : filterOption}
      noOptionsMessage={loading ? loadingMessage || defaultLoadingMessage : noOptionsMessage || defaultNoOptionsMessage}
      isClearable={isClearable}
      isMulti={isMulti}
      menuPlacement="auto"
      menuPosition="absolute"
      onChange={handleOptionSelect}
      placeholder={defaultPlaceholder}
      value={value}
      {...other}
      onBlur={onBlur}
    />
  );
}

export default withStyles(styles)(Select);
